import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Input } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';

import { getThemeColor } from '../../utils/common';
import { FORTA_WHITE } from '../../utils/colors';
import { debounce } from '../../helpers';
import { Themes } from '../../constants';

interface CounterButtonProps {
  count: number;
  colorTheme?: Themes;
  disabled?: boolean;
  delay?: number;
  onFocus?: (isFocused: boolean) => void;
  onCountChange: (count: number) => void;
  dataTestId?: string;
}

interface StyledButtonProps {
  disableRipple: true;
  onClick: () => void;
}

const StyledCounterButton = styled(Button)<StyledButtonProps>(() => ({
  height: 40,
  minWidth: 40,
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

const CountDisplay = styled(Input, { shouldForwardProp: (prop) => prop !== 'colorTheme' })<{
  colorTheme?: Themes;
}>(({ colorTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 60,
  height: 40,
  backgroundColor: getThemeColor(colorTheme).light1,
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 'bold',
  '& input': {
    textAlign: 'center',
    color: FORTA_WHITE,
    fontWeight: 'bold',
    fontSize: '16px',
    outline: 'none',
    border: 'none',
  },
  // Remove underline for various states
  '& .MuiInput-underline:before, & .MuiInput-underline:after, &:hover:not(.Mui-disabled):before, &:focus-within:not(.Mui-disabled):before, &:not(.Mui-disabled):before, &.Mui-focused:not(.Mui-disabled):before,&.Mui-focused:not(.Mui-disabled):after':
    {
      borderBottom: 'none',
    },
  '& .MuiInputBase-input.Mui-disabled': {
    textAlign: 'center',
    color: FORTA_WHITE,
    fontWeight: 'bold',
    fontSize: '16px',
    outline: 'none',
    border: 'none',
    WebkitTextFillColor: FORTA_WHITE,
  },
}));

const CounterContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'colorTheme' })<{
  colorTheme?: Themes;
}>(({ colorTheme }) => ({
  display: 'flex',
  height: 40,
  width: 172,
  alignItems: 'center',
  backgroundColor: FORTA_WHITE,
  border: `1px solid ${getThemeColor(colorTheme).light2}`,
  justifyContent: 'space-between',
  padding: '0 8px',
  borderRadius: '8px',
}));

const FHCounter: React.FC<CounterButtonProps> = (props: CounterButtonProps) => {
  const {
    count = 0,
    disabled = false,
    colorTheme = Themes.Primary,
    delay = 250,
    onCountChange = (_count: number) => {},
    onFocus,
    dataTestId = '',
  } = props;

  const [inputCount, setInputCount] = useState(0);

  const debouncedCountChange = useCallback(debounce(onCountChange, delay), [onCountChange]);

  useEffect(() => {
    setInputCount(count);
  }, [count]);

  const handleInputCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCount = parseInt(event.target.value, 10) || 0;
    setInputCount(newCount);
  };

  const handleBlur = () => {
    if (!disabled) {
      debouncedCountChange(inputCount);
      onFocus?.(false);
    }
  };

  const handleIncrement = () => {
    if (!disabled) {
      debouncedCountChange(inputCount + 1);
      setInputCount(inputCount + 1);
    }
  };

  const handleDecrement = () => {
    if (!disabled) {
      if (inputCount > 0) {
        debouncedCountChange(inputCount - 1);
        setInputCount(inputCount - 1);
      }
    }
  };

  return (
    <CounterContainer colorTheme={colorTheme}>
      <StyledCounterButton
        data-testid={`counterDecrement_${dataTestId.replace(/ /g, '_')}`}
        disableRipple
        disabled={disabled}
        onClick={handleDecrement}
      >
        <RemoveCircleOutline />
      </StyledCounterButton>
      <CountDisplay
        data-testid={`counterValue${dataTestId}`}
        sx={{ textAlign: 'center' }}
        colorTheme={colorTheme}
        type="text"
        value={inputCount?.toString()?.padStart(2, '0')}
        inputProps={{
          min: 0,
        }}
        onBlur={handleBlur}
        onFocus={() => {
          if (disabled) return;
          onFocus?.(true);
        }}
        disabled={disabled}
        onChange={handleInputCountChange}
      />
      <StyledCounterButton
        data-testid={`counterIncrement${dataTestId.replace(/ /g, '_')}`}
        disableRipple
        disabled={disabled}
        onClick={handleIncrement}
      >
        <AddCircleOutline />
      </StyledCounterButton>
    </CounterContainer>
  );
};

export default FHCounter;
